// frontend/src/components/Shared/AudioDevicePicker.jsx
// Reusable microphone / audio device selector dropdown.
// Supports: built-in mic, USB mic, VB-Cable, Voicemeeter, BlackHole, etc.
import React, { useState } from 'react';

// Guess device category from label for icon + badge
function categorize(label = '') {
  const l = label.toLowerCase();
  if (l.includes('vb-cable') || l.includes('vb cable'))   return { icon: '🔀', badge: 'Virtual', color: 'text-purple-600' };
  if (l.includes('voicemeeter'))                           return { icon: '🎛️', badge: 'Virtual', color: 'text-purple-600' };
  if (l.includes('blackhole'))                             return { icon: '🕳️', badge: 'Virtual', color: 'text-purple-600' };
  if (l.includes('loopback'))                              return { icon: '🔁', badge: 'Loopback', color: 'text-indigo-600' };
  if (l.includes('bluetooth') || l.includes('airpod') ||
      l.includes('wireless'))                              return { icon: '📡', badge: 'Bluetooth', color: 'text-blue-600' };
  if (l.includes('usb') || l.includes('external') ||
      l.includes('condenser') || l.includes('yeti') ||
      l.includes('blue') || l.includes('rode') ||
      l.includes('shure') || l.includes('audio-technica')) return { icon: '🎙️', badge: 'USB', color: 'text-green-600' };
  if (l.includes('headset') || l.includes('headphone') ||
      l.includes('3.5') || l.includes('jack'))             return { icon: '🎧', badge: 'Headset', color: 'text-teal-600' };
  if (l.includes('built') || l.includes('internal') ||
      l.includes('laptop') || l.includes('array'))         return { icon: '💻', badge: 'Built-in', color: 'text-gray-500' };
  return { icon: '🎤', badge: 'Mic', color: 'text-gray-500' };
}

function friendlyLabel(device, index) {
  if (!device.label) return `Microphone ${index + 1}`;
  // Remove common OS noise like "(hw:0,0)" or long IDs in parentheses
  return device.label.replace(/\s*\([0-9a-f:,\s]{6,}\)\s*/gi, '').trim() || device.label;
}

// ────────────────────────────────────────────────────────────────────────────
// Props:
//   devices        — MediaDeviceInfo[] from useAudioDevices
//   deviceId       — currently selected deviceId ('' = system default)
//   onSelect       — (deviceId: string) => void
//   disabled       — grey out while recording
//   compact        — true = small inline version (for StealthAgent dark theme)
//   dark           — true = dark background styling
// ────────────────────────────────────────────────────────────────────────────
const AudioDevicePicker = ({ devices = [], deviceId = '', onSelect, disabled = false, compact = false, dark = false }) => {
  const [open, setOpen] = useState(false);

  const selectedDev = devices.find(d => d.deviceId === deviceId) || null;
  const selectedCat = selectedDev ? categorize(selectedDev.label) : { icon: '🎤', badge: 'Default' };
  const selectedLabel = selectedDev ? friendlyLabel(selectedDev, devices.indexOf(selectedDev)) : 'System Default';

  const handleSelect = (id) => {
    onSelect(id);
    setOpen(false);
  };

  if (compact) {
    // ── Compact dark version for StealthAgent ──────────────────────────────
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setOpen(o => !o)}
          disabled={disabled}
          title="Select audio input device"
          className={`flex items-center space-x-1.5 px-2 py-1 rounded text-xs border transition-colors
            ${disabled
              ? 'opacity-40 cursor-not-allowed border-gray-800 bg-gray-900 text-gray-600'
              : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-500 hover:text-gray-100 cursor-pointer'
            }`}
        >
          <span className="text-sm">{selectedCat.icon}</span>
          <span className="max-w-[100px] truncate">{selectedLabel}</span>
          <svg className={`h-3 w-3 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
            <div className="absolute bottom-full left-0 mb-1 z-20 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl overflow-hidden min-w-[200px]">
              {/* Default option */}
              <button
                onClick={() => handleSelect('')}
                className={`w-full flex items-center space-x-2 px-3 py-2 text-xs text-left transition-colors hover:bg-gray-800
                  ${!deviceId ? 'bg-gray-800 text-blue-400' : 'text-gray-300'}`}
              >
                <span>🎤</span>
                <span className="flex-1">System Default</span>
                {!deviceId && <span className="text-blue-400">✓</span>}
              </button>

              {devices.length > 0 && <div className="border-t border-gray-800" />}

              {devices.map((dev, i) => {
                const cat   = categorize(dev.label);
                const label = friendlyLabel(dev, i);
                const isSelected = dev.deviceId === deviceId;
                return (
                  <button
                    key={dev.deviceId}
                    onClick={() => handleSelect(dev.deviceId)}
                    className={`w-full flex items-center space-x-2 px-3 py-2 text-xs text-left transition-colors hover:bg-gray-800
                      ${isSelected ? 'bg-gray-800 text-blue-400' : 'text-gray-300'}`}
                  >
                    <span>{cat.icon}</span>
                    <div className="flex-1 min-w-0">
                      <span className="block truncate">{label}</span>
                      <span className={`text-xs opacity-60 ${cat.color}`}>{cat.badge}</span>
                    </div>
                    {isSelected && <span className="text-blue-400 flex-shrink-0">✓</span>}
                  </button>
                );
              })}

              {devices.length === 0 && (
                <p className="px-3 py-2 text-xs text-gray-600 italic">No devices found</p>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Standard light version for VoiceRecorder / VoiceEnrollment ────────────
  return (
    <div className="relative w-full">
      <label className={`block text-xs font-medium mb-1 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
        🎤 Audio Input Device
      </label>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className={`w-full flex items-center space-x-2 px-3 py-2.5 rounded-lg border text-sm text-left transition-all
          ${disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:border-indigo-400'
          }
          ${dark
            ? 'bg-gray-800 border-gray-600 text-gray-200'
            : 'bg-white border-gray-300 text-gray-800'
          }`}
      >
        <span className="text-lg">{selectedCat.icon}</span>
        <div className="flex-1 min-w-0">
          <span className="block truncate font-medium">{selectedLabel}</span>
          <span className={`text-xs ${selectedCat.color} opacity-80`}>{selectedCat.badge}</span>
        </div>
        <svg className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={`absolute top-full left-0 right-0 mt-1 z-20 rounded-xl shadow-2xl border overflow-hidden
            ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>

            {/* System Default */}
            <button
              onClick={() => handleSelect('')}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-sm text-left transition-colors
                ${!deviceId
                  ? dark ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-700'
                  : dark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                }`}
            >
              <span className="text-xl">🎤</span>
              <div className="flex-1">
                <span className="font-medium block">System Default</span>
                <span className="text-xs text-gray-400">Browser-selected microphone</span>
              </div>
              {!deviceId && <span className={dark ? 'text-blue-400' : 'text-blue-600'}>✓</span>}
            </button>

            {devices.length > 0 && (
              <div className={`border-t ${dark ? 'border-gray-700' : 'border-gray-100'}`}>
                <p className={`px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider
                  ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Available Devices ({devices.length})
                </p>
              </div>
            )}

            {devices.map((dev, i) => {
              const cat   = categorize(dev.label);
              const label = friendlyLabel(dev, i);
              const isSelected = dev.deviceId === deviceId;
              // Highlight virtual audio devices with a special background
              const isVirtual = cat.badge === 'Virtual' || cat.badge === 'Loopback';
              return (
                <button
                  key={dev.deviceId}
                  onClick={() => handleSelect(dev.deviceId)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-sm text-left transition-colors
                    ${isSelected
                      ? dark ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isVirtual
                        ? dark ? 'hover:bg-purple-950 text-purple-300' : 'hover:bg-purple-50 text-purple-700'
                        : dark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                    }`}
                >
                  <span className="text-xl">{cat.icon}</span>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium block truncate">{label}</span>
                    <span className={`text-xs ${cat.color}`}>{cat.badge}</span>
                    {isVirtual && (
                      <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                        Captures speaker output
                      </span>
                    )}
                  </div>
                  {isSelected && <span className={dark ? 'text-blue-400' : 'text-blue-600'}>✓</span>}
                </button>
              );
            })}

            {devices.length === 0 && (
              <p className={`px-4 py-3 text-sm italic ${dark ? 'text-gray-600' : 'text-gray-400'}`}>
                No microphone devices found
              </p>
            )}

            {/* Hint row */}
            <div className={`px-4 py-2.5 border-t text-xs
              ${dark ? 'border-gray-800 text-gray-600' : 'border-gray-100 text-gray-400'}`}>
              💡 To capture speaker output, install{' '}
              <a href="https://vb-audio.com/Cable/" target="_blank" rel="noreferrer"
                className="underline text-purple-500 hover:text-purple-400">
                VB-Cable
              </a>{' '}
              (Windows) or{' '}
              <a href="https://existential.audio/blackhole/" target="_blank" rel="noreferrer"
                className="underline text-purple-500 hover:text-purple-400">
                BlackHole
              </a>{' '}
              (macOS) and select it here
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AudioDevicePicker;
